import { useMeDelete } from "@/api";
import { Button } from "@/components/ui/button";
import Dialog from "@/components/ui/dialog";
import { LoadingCircle } from "@/components/ui/loadingCircle";
import { toast } from "@/components/ui/use-toast";
import { DialogClose } from "@radix-ui/react-dialog";
import { useNavigate } from "@tanstack/react-router";
import { Dispatch, SetStateAction } from "react";

interface Props {
  setOpen: Dispatch<SetStateAction<boolean>>;
}

export function DeleteMeModal({ setOpen }: Props) {
  const [deleteMe, { loading }] = useMeDelete();
  const navigate = useNavigate();

  function handleDeleteMe() {
    deleteMe().then(({ data, errors }) => {
      if (!data || errors) {
        console.error("Unable to delete user account", { data, errors });
        return toast({
          title: "Unable to delete your account",
          description:
            "An error occurred while trying to delete your account. Please try again or report the issue.",
          variant: "destructive"
        });
      }

      const typename = data.meDelete.__typename;

      if (typename === "UserDeleted") {
        navigate({ to: "/account-deleted" });
        return undefined;
      }
      if (typename === "UserHasSoleAdminship") {
        return toast({
          title:
            "An account who is sole admin in an organization cannot be deleted",
          description:
            "To delete your account, first delete all organizations where you are the only admin.",
          variant: "destructive",
          duration: 15000
        });
      }

      return toast({
        title: "Unable to delete your account",
        description:
          "An error occurred while trying to delete your account. Please try again or report the issue.",
        variant: "destructive"
      });
    });
  }

  return (
    <Dialog.Root open onOpenChange={setOpen}>
      <Dialog.Content>
        <Dialog.Header>
          <Dialog.Title>Delete account</Dialog.Title>
          <Dialog.Description>
            Are you sure you want to delete your Numerous account? This action
            cannot be undone.
          </Dialog.Description>
        </Dialog.Header>

        <Dialog.Footer>
          <DialogClose asChild>
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="destructive"
            disabled={loading}
            onClick={handleDeleteMe}
          >
            Delete
            {loading && <LoadingCircle className="ml-2" />}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
}
